import React from 'react';

const RefundPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="bg-primary-600 px-6 py-4">
            <h1 className="text-3xl font-bold text-white">Cancellation and Refund Policy</h1>
            <p className="text-primary-100 mt-2">Last Updated: December 15, 2023</p>
          </div>
          
          <div className="px-6 py-8 prose prose-lg max-w-none">
            <h2>1. Overview</h2>
            <p>This Cancellation and Refund Policy explains how you can cancel your Crawly Carl subscription and request refunds. We strive to provide fair and transparent refund policies while ensuring the sustainability of our services.</p>
            <p>This policy applies to all Crawly Carl services, including our web scraping API, dashboard access, and any additional features or tools we may offer.</p>

            <h2>2. Subscription Cancellation</h2>
            
            <h3>2.1 How to Cancel</h3>
            <p>You can cancel your subscription at any time through one of the following methods:</p>
            <ul>
              <li>Log into your dashboard at console.crawlycarl.com and navigate to your billing settings</li>
              <li>Contact our support team at support@crawlycarl.com</li>
              <li>Use the cancellation link provided in your subscription confirmation emails</li>
            </ul>
            
            <h3>2.2 Immediate Effect</h3>
            <p>When you cancel your subscription:</p>
            <ul>
              <li>Your subscription will remain active until the end of your current billing period</li>
              <li>You will continue to have access to all paid features until the subscription expires</li>
              <li>No future charges will be made to your payment method</li>
              <li>Your account will automatically downgrade to the free tier (if available) at the end of the billing period</li>
            </ul>

            <h2>3. Refund Eligibility</h2>
            
            <h3>3.1 General Refund Policy</h3>
            <p>Refunds may be provided in the following circumstances:</p>
            <ul>
              <li><strong>Service Outages:</strong> Extended service unavailability due to technical issues on our end</li>
              <li><strong>Billing Errors:</strong> Incorrect charges or duplicate transactions</li>
              <li><strong>Early Cancellation:</strong> Cancellation within 7 days of initial subscription with minimal usage</li>
              <li><strong>Service Non-Delivery:</strong> Failure to provide contracted services</li>
              <li><strong>Technical Issues:</strong> Unresolved technical problems preventing service use</li>
            </ul>
            
            <h3>3.2 Non-Refundable Items</h3>
            <p>The following are generally not eligible for refunds:</p>
            <ul>
              <li>API credits that have been consumed</li>
              <li>Subscriptions cancelled after 30 days with regular usage</li>
              <li>Overage charges for exceeding plan limits</li>
              <li>Custom development or consulting services</li>
              <li>Third-party service charges</li>
            </ul>

            <h2>4. Refund Process</h2>
            
            <h3>4.1 How to Request a Refund</h3>
            <p>To request a refund:</p>
            <ol>
              <li>Contact our support team at billing@crawlycarl.com</li>
              <li>Provide your account details and reason for refund request</li>
              <li>Include any relevant documentation or error messages</li>
              <li>Our team will review your request within 3-5 business days</li>
            </ol>
            
            <h3>4.2 Refund Review Process</h3>
            <p>Our refund review process includes:</p>
            <ul>
              <li>Verification of account and payment information</li>
              <li>Review of service usage and any technical issues</li>
              <li>Assessment of refund eligibility based on our policy</li>
              <li>Communication of decision within 5 business days</li>
            </ul>

            <h2>5. Credit Balance Refunds</h2>
            
            <h3>5.1 Unused Credits</h3>
            <p>Regarding unused API credits:</p>
            <ul>
              <li>Credits purchased within the last 30 days may be eligible for refund</li>
              <li>Promotional or bonus credits are non-refundable</li>
              <li>Credits must be substantially unused (less than 10% consumption)</li>
              <li>Refund amount is based on the proportional unused credit value</li>
            </ul>

            <h2>6. Processing Times</h2>
            
            <h3>6.1 Refund Processing</h3>
            <p>Once a refund is approved:</p>
            <ul>
              <li>Credit card refunds: 5-10 business days</li>
              <li>PayPal refunds: 3-5 business days</li>
              <li>Bank transfer refunds: 7-14 business days</li>
              <li>Account credit refunds: Immediate</li>
            </ul>

            <h2>7. Contact Us</h2>
            <p>For questions about cancellations, refunds, or this policy, contact us:</p>
            
            <div className="bg-gray-100 p-4 rounded-lg mt-4">
              <p><strong>Email:</strong> billing@crawlycarl.com</p>
              <p><strong>Support:</strong> support@crawlycarl.com</p>
              <p><strong>Phone:</strong> +1 (555) 123-4567</p>
              <p><strong>Business Hours:</strong> Monday-Friday, 9 AM - 6 PM PST</p>
              <p><strong>Address:</strong> Crawly Carl<br />
              1234 Market Street<br />
              San Francisco, CA 94103</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPage;